import { env } from "@shared/config.ts";

const magicUUID = crypto.randomUUID();

export const routes = {
  root: "/",
  unifiedRoot: "/u/",
  home() {
    return routes.root;
  },
  error() {
    return routes.root.concat("error-page/");
  },
  page_not_found() {
    return routes.root.concat("page-not-found/");
  },
  projects() {
    return routes.root.concat("projects/");
  },
  project: {
    root({ projectId = ":projectId" }) {
      return routes.root.concat(`projects/${projectId}/`);
    },
    objects(projectId = ":projectId") {
      return routes.project.root({ projectId }).concat("objects/");
    },
    requisites(projectId = ":projectId") {
      return routes.project.root({ projectId }).concat("requisites/");
    },
    statistics(projectId = ":projectId") {
      return routes.project.root({ projectId }).concat("statistics/");
    },
    docstore(projectId = ":projectId") {
      return routes.project
        .root({ projectId })
        .concat(`contracts/${magicUUID}/docstore`);
    },
    actRegistry(projectId = ":projectId") {
      return `${env.OLD_FRONTEND_URL}/projects/${projectId}/registry-of-acts`;
    },
    journals(projectId = ":projectId") {
      return routes.project.root({ projectId }).concat("journals/");
    },
    wildcard(projectId = ":projectId") {
      return routes.project.root({ projectId }).concat("/*");
    },
  },
  general: {
    root(projectId = ":projectId") {
      return routes.project.root({ projectId });
    },
    members(projectId = ":projectId") {
      return routes.general.root(projectId).concat("members/");
    },
    related(projectId = ":projectId") {
      return routes.general.root(projectId).concat("related/");
    },
    settings(projectId = ":projectId") {
      return routes.general.root(projectId).concat("settings/");
    },
    history(projectId = ":projectId") {
      return routes.general.root(projectId).concat("history/");
    },
    recordsRegister(projectId = ":projectId") {
      return routes.general.root(projectId).concat("records-register/");
    },
  },
  objects: {
    root(projectId = ":projectId") {
      return routes.project.objects(projectId);
    },
    object(projectId = ":projectId", objectId = ":objectId") {
      return routes.objects.root(projectId).concat(`${objectId}/`);
    },
    users(projectId = ":projectId", objectId = ":objectId") {
      return routes.objects.object(projectId, objectId).concat("users/");
    },
    journals(projectId = ":projectId", objectId = ":objectId") {
      return routes.objects.object(projectId, objectId).concat("journals/");
    },
    settings(projectId = ":projectId", objectId = ":objectId") {
      return routes.objects.object(projectId, objectId).concat("settings/");
    },
    edit(projectId = ":projectId", objectId = ":objectId") {
      return routes.objects.object(projectId, objectId).concat("edit/");
    },
  },
  journals: {
    root(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
    ) {
      return routes.project
        .root({ projectId })
        .concat(`object/${objectId}/mainJournalId/${mainJournalId}/`);
    },
    title(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
    ) {
      return routes.journals
        .root(projectId, objectId, mainJournalId)
        .concat(`titleJournal/${titleId}`);
    },
    notes(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
    ) {
      return routes.journals
        .root(projectId, objectId, mainJournalId)
        .concat(`titleJournal/${titleId}/notes`);
    },
    details(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
    ) {
      return routes.journals
        .root(projectId, objectId, mainJournalId)
        .concat(`titleJournal/${titleId}/details`);
    },
    amendments(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
    ) {
      return routes.journals
        .root(projectId, objectId, mainJournalId)
        .concat(`titleJournal/${titleId}/amendments`);
    },
    settings(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
    ) {
      return routes.journals
        .root(projectId, objectId, mainJournalId)
        .concat(`settings/`);
    },
    version(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
      journalVersionId?: string,
    ) {
      const suffix = journalVersionId ? `/${journalVersionId}` : "";
      return routes.journals
        .root(projectId, objectId, mainJournalId)
        .concat(`version/${titleId}${suffix}/`);
    },
    history(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
    ) {
      return routes.journals
        .root(projectId, objectId, mainJournalId)
        .concat(`history/${titleId}`);
    },
    section1(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
    ) {
      return routes.journals
        .root(projectId, objectId, mainJournalId)
        .concat(`${titleId}/section1/`);
    },
    section1Form(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
      itemNoVersionId = ":itemNoVersionId?",
    ) {
      return routes.journals
        .section1(projectId, objectId, mainJournalId, titleId)
        .concat(`form/${itemNoVersionId || "new"}`);
    },
    section1View(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
      itemNoVersionId = ":itemNoVersionId",
    ) {
      return routes.journals
        .section1(projectId, objectId, mainJournalId, titleId)
        .concat(`view/${itemNoVersionId}`);
    },
    section1Personnel(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
    ) {
      return routes.journals
        .section1(projectId, objectId, mainJournalId, titleId)
        .concat("personnel/");
    },
    section2(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
    ) {
      return routes.journals
        .root(projectId, objectId, mainJournalId)
        .concat(`${titleId}/section2/`);
    },
    section2Form(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
      itemNoVersionId = ":itemNoVersionId?",
    ) {
      return routes.journals
        .section2(projectId, objectId, mainJournalId, titleId)
        .concat(`form/${itemNoVersionId || "new"}`);
    },
    section2View(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
      itemNoVersionId = ":itemNoVersionId",
    ) {
      return routes.journals
        .section2(projectId, objectId, mainJournalId, titleId)
        .concat(`view/${itemNoVersionId}`);
    },
    section2Personnel(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
    ) {
      return routes.journals
        .section2(projectId, objectId, mainJournalId, titleId)
        .concat("personnel/");
    },
    section3(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
    ) {
      return routes.journals
        .root(projectId, objectId, mainJournalId)
        .concat(`${titleId}/section3/`);
    },
    section3Form(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
      itemNoVersionId = ":itemNoVersionId?",
    ) {
      return routes.journals
        .section3(projectId, objectId, mainJournalId, titleId)
        .concat(`form/${itemNoVersionId || "new"}`);
    },
    section3View(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
      itemNoVersionId = ":itemNoVersionId",
    ) {
      return routes.journals
        .section3(projectId, objectId, mainJournalId, titleId)
        .concat(`view/${itemNoVersionId}`);
    },
    section3Personnel(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
    ) {
      return routes.journals
        .section3(projectId, objectId, mainJournalId, titleId)
        .concat("personnel/");
    },
    section4(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
    ) {
      return routes.journals
        .root(projectId, objectId, mainJournalId)
        .concat(`${titleId}/section4/`);
    },
    section4Form(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
      itemNoVersionId = ":itemNoVersionId?",
    ) {
      return routes.journals
        .section4(projectId, objectId, mainJournalId, titleId)
        .concat(`form/${itemNoVersionId || "new"}`);
    },
    section4View(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
      itemNoVersionId = ":itemNoVersionId",
    ) {
      return routes.journals
        .section4(projectId, objectId, mainJournalId, titleId)
        .concat(`view/${itemNoVersionId}`);
    },
    section4Personnel(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
    ) {
      return routes.journals
        .section4(projectId, objectId, mainJournalId, titleId)
        .concat("personnel/");
    },
    section5(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
    ) {
      return routes.journals
        .root(projectId, objectId, mainJournalId)
        .concat(`${titleId}/section5/`);
    },
    section6(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
    ) {
      return routes.journals
        .root(projectId, objectId, mainJournalId)
        .concat(`${titleId}/section6/`);
    },
    versions(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
    ) {
      return routes.journals
        .root(projectId, objectId, mainJournalId)
        .concat(`versions/${titleId}/`);
    },
    journalVersionTitle(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
      mainJournalVersionId = ":mainJournalVersionId",
    ) {
      return routes.journals
        .versions(projectId, objectId, mainJournalId, titleId)
        .concat(`${mainJournalVersionId}/titleJournal`);
    },
    journalVersionSection1(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
      mainJournalVersionId = ":mainJournalVersionId",
    ) {
      return routes.journals
        .versions(projectId, objectId, mainJournalId, titleId)
        .concat(`${mainJournalVersionId}/section1/`);
    },
    journalVersionSection1Personnel(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
      mainJournalVersionId = ":mainJournalVersionId",
    ) {
      return routes.journals
        .journalVersionSection1(
          projectId,
          objectId,
          mainJournalId,
          titleId,
          mainJournalVersionId,
        )
        .concat("personnel/");
    },
    journalVersionSection2(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
      mainJournalVersionId = ":mainJournalVersionId",
    ) {
      return routes.journals
        .versions(projectId, objectId, mainJournalId, titleId)
        .concat(`${mainJournalVersionId}/section2/`);
    },
    journalVersionSection2Personnel(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
      mainJournalVersionId = ":mainJournalVersionId",
    ) {
      return routes.journals
        .journalVersionSection2(
          projectId,
          objectId,
          mainJournalId,
          titleId,
          mainJournalVersionId,
        )
        .concat("personnel/");
    },
    journalVersionSection3(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
      mainJournalVersionId = ":mainJournalVersionId",
    ) {
      return routes.journals
        .versions(projectId, objectId, mainJournalId, titleId)
        .concat(`${mainJournalVersionId}/section3/`);
    },
    journalVersionSection3Personnel(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
      mainJournalVersionId = ":mainJournalVersionId",
    ) {
      return routes.journals
        .journalVersionSection3(
          projectId,
          objectId,
          mainJournalId,
          titleId,
          mainJournalVersionId,
        )
        .concat("personnel/");
    },
    journalVersionSection4(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
      mainJournalVersionId = ":mainJournalVersionId",
    ) {
      return routes.journals
        .versions(projectId, objectId, mainJournalId, titleId)
        .concat(`${mainJournalVersionId}/section4/`);
    },
    journalVersionSection4Personnel(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
      mainJournalVersionId = ":mainJournalVersionId",
    ) {
      return routes.journals
        .journalVersionSection4(
          projectId,
          objectId,
          mainJournalId,
          titleId,
          mainJournalVersionId,
        )
        .concat("personnel/");
    },
    journalVersionSection5(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
      mainJournalVersionId = ":mainJournalVersionId",
    ) {
      return routes.journals
        .versions(projectId, objectId, mainJournalId, titleId)
        .concat(`${mainJournalVersionId}/section5/`);
    },
    journalVersionSection6(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
      mainJournalVersionId = ":mainJournalVersionId",
    ) {
      return routes.journals
        .versions(projectId, objectId, mainJournalId, titleId)
        .concat(`${mainJournalVersionId}/section6/`);
    },
    amendmentsForm(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
    ) {
      return routes.journals
        .amendments(projectId, objectId, mainJournalId, titleId)
        .concat("/new");
    },
    amendmentsView(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
      requestId = ":requestId",
    ) {
      return routes.journals
        .amendments(projectId, objectId, mainJournalId, titleId)
        .concat(`/view/${requestId}`);
    },
    titleVersionDetail(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
      titleVersionId = ":titleVersionId",
    ) {
      return routes.journals
        .title(projectId, objectId, mainJournalId, titleId)
        .concat(`/version/${titleVersionId}`);
    },
    journalVersions(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
    ) {
      return routes.journals
        .root(projectId, objectId, mainJournalId)
        .concat(`version/${titleId}/`);
    },
    journalVersion(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
      versionId = ":versionId",
    ) {
      return routes.journals
        .root(projectId, objectId, mainJournalId)
        .concat(`version/${titleId}/${versionId}/`);
    },
    titleVersions(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
    ) {
      return routes.journals
        .title(projectId, objectId, mainJournalId, titleId)
        .concat(`/title-versions/`);
    },
    titleVersion(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
      titleVersionId = ":titleVersionId",
    ) {
      return routes.journals
        .title(projectId, objectId, mainJournalId, titleId)
        .concat(`/title-versions/${titleVersionId}/`);
    },
    titleChangeRequests(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
    ) {
      return routes.journals
        .title(projectId, objectId, mainJournalId, titleId)
        .concat(`/title-change-requests/`);
    },
    titleChangeRequestNew(
      projectId = ":projectId",
      objectId = ":objectId",
      mainJournalId = ":mainJournalId",
      titleId = ":titleId",
    ) {
      return routes.journals
        .title(projectId, objectId, mainJournalId, titleId)
        .concat(`/title-change-requests/new/`);
    },
  },
  supervision: {
    root(projectId = ":projectId") {
      return routes.project.root({ projectId }).concat("supervision/");
    },
    notifications(
      projectId = ":projectId",
      supervisionId = ":supervisionId",
    ) {
      return routes.supervision.root(projectId).concat(`${supervisionId}/notifications/`);
    },
    adminSettings(
      projectId = ":projectId",
      supervisionId = ":supervisionId",
    ) {
      return routes.supervision
        .root(projectId)
        .concat(`${supervisionId}/admin-settings/`);
    },
    integrationSettings(
      projectId = ":projectId",
      supervisionId = ":supervisionId",
    ) {
      return routes.supervision
        .root(projectId)
        .concat(`${supervisionId}/integration-settings/`);
    },
  },
  organization() {
    return routes.root.concat("organization/");
  },
  profile() {
    return routes.root.concat("my-account/");
  },
  account() {
    return routes.root.concat("account/");
  },
  // Unified routes for iframe embedding (without header, footer, and sidebar)
  unified: {
    project: {
      root({ projectId = ":projectId" }) {
        return routes.unifiedRoot.concat(`projects/${projectId}/`);
      },
      objects(projectId = ":projectId") {
        return routes.unified.project.root({ projectId }).concat("objects/");
      },
      requisites(projectId = ":projectId") {
        return routes.unified.project.root({ projectId }).concat("requisites/");
      },
      statistics(projectId = ":projectId") {
        return routes.unified.project.root({ projectId }).concat("statistics/");
      },
      docstore(projectId = ":projectId") {
        return routes.unified.project
          .root({ projectId })
          .concat(`contracts/${magicUUID}/docstore`);
      },
      actRegistry(projectId = ":projectId") {
        return `${env.OLD_FRONTEND_URL}/u/projects/${projectId}/registry-of-acts`;
      },
      journals(projectId = ":projectId") {
        return routes.unified.project.root({ projectId }).concat("journals/");
      },
    },
    general: {
      root(projectId = ":projectId") {
        return routes.unified.project.root({ projectId });
      },
      members(projectId = ":projectId") {
        return routes.unified.general.root(projectId).concat("members/");
      },
      related(projectId = ":projectId") {
        return routes.unified.general.root(projectId).concat("related/");
      },
      settings(projectId = ":projectId") {
        return routes.unified.general.root(projectId).concat("settings/");
      },
      history(projectId = ":projectId") {
        return routes.unified.general.root(projectId).concat("history/");
      },
      recordsRegister(projectId = ":projectId") {
        return routes.unified.general.root(projectId).concat("records-register/");
      },
    },
    objects: {
      root(projectId = ":projectId") {
        return routes.unified.project.objects(projectId);
      },
      object(projectId = ":projectId", objectId = ":objectId") {
        return routes.unified.objects.root(projectId).concat(`${objectId}/`);
      },
      users(projectId = ":projectId", objectId = ":objectId") {
        return routes.unified.objects.object(projectId, objectId).concat("users/");
      },
      journals(projectId = ":projectId", objectId = ":objectId") {
        return routes.unified.objects.object(projectId, objectId).concat("journals/");
      },
      settings(projectId = ":projectId", objectId = ":objectId") {
        return routes.unified.objects.object(projectId, objectId).concat("settings/");
      },
      edit(projectId = ":projectId", objectId = ":objectId") {
        return routes.unified.objects.object(projectId, objectId).concat("edit/");
      },
    },
    journals: {
      root(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
      ) {
        return routes.unified.project
          .root({ projectId })
          .concat(`object/${objectId}/mainJournalId/${mainJournalId}/`);
      },
      title(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
      ) {
        return routes.unified.journals
          .root(projectId, objectId, mainJournalId)
          .concat(`titleJournal/${titleId}`);
      },
      notes(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
      ) {
        return routes.unified.journals
          .root(projectId, objectId, mainJournalId)
          .concat(`titleJournal/${titleId}/notes`);
      },
      details(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
      ) {
        return routes.unified.journals
          .root(projectId, objectId, mainJournalId)
          .concat(`titleJournal/${titleId}/details`);
      },
      amendments(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
      ) {
        return routes.unified.journals
          .root(projectId, objectId, mainJournalId)
          .concat(`titleJournal/${titleId}/amendments`);
      },
      amendmentsForm(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
      ) {
        return routes.unified.journals
          .amendments(projectId, objectId, mainJournalId, titleId)
          .concat("/new");
      },
      amendmentsView(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
        requestId = ":requestId",
      ) {
        return routes.unified.journals
          .amendments(projectId, objectId, mainJournalId, titleId)
          .concat(`/view/${requestId}`);
      },
      settings(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
      ) {
        return routes.unified.journals
          .root(projectId, objectId, mainJournalId)
          .concat(`settings/`);
      },
      history(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
      ) {
        return routes.unified.journals
          .root(projectId, objectId, mainJournalId)
          .concat(`history/${titleId}`);
      },
      section1(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
      ) {
        return routes.unified.journals
          .root(projectId, objectId, mainJournalId)
          .concat(`${titleId}/section1/`);
      },
      section1Form(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
        itemNoVersionId = ":itemNoVersionId?",
      ) {
        return routes.unified.journals
          .section1(projectId, objectId, mainJournalId, titleId)
          .concat(`form/${itemNoVersionId || "new"}`);
      },
      section1View(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
        itemNoVersionId = ":itemNoVersionId",
      ) {
        return routes.unified.journals
          .section1(projectId, objectId, mainJournalId, titleId)
          .concat(`view/${itemNoVersionId}`);
      },
      section1Personnel(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
      ) {
        return routes.unified.journals
          .section1(projectId, objectId, mainJournalId, titleId)
          .concat("personnel/");
      },
      section2(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
      ) {
        return routes.unified.journals
          .root(projectId, objectId, mainJournalId)
          .concat(`${titleId}/section2/`);
      },
      section2Form(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
        itemNoVersionId = ":itemNoVersionId?",
      ) {
        return routes.unified.journals
          .section2(projectId, objectId, mainJournalId, titleId)
          .concat(`form/${itemNoVersionId || "new"}`);
      },
      section2View(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
        itemNoVersionId = ":itemNoVersionId",
      ) {
        return routes.unified.journals
          .section2(projectId, objectId, mainJournalId, titleId)
          .concat(`view/${itemNoVersionId}`);
      },
      section2Personnel(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
      ) {
        return routes.unified.journals
          .section2(projectId, objectId, mainJournalId, titleId)
          .concat("personnel/");
      },
      section3(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
      ) {
        return routes.unified.journals
          .root(projectId, objectId, mainJournalId)
          .concat(`${titleId}/section3/`);
      },
      section3Form(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
        itemNoVersionId = ":itemNoVersionId?",
      ) {
        return routes.unified.journals
          .section3(projectId, objectId, mainJournalId, titleId)
          .concat(`form/${itemNoVersionId || "new"}`);
      },
      section3View(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
        itemNoVersionId = ":itemNoVersionId",
      ) {
        return routes.unified.journals
          .section3(projectId, objectId, mainJournalId, titleId)
          .concat(`view/${itemNoVersionId}`);
      },
      section3Personnel(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
      ) {
        return routes.unified.journals
          .section3(projectId, objectId, mainJournalId, titleId)
          .concat("personnel/");
      },
      section4(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
      ) {
        return routes.unified.journals
          .root(projectId, objectId, mainJournalId)
          .concat(`${titleId}/section4/`);
      },
      section4Form(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
        itemNoVersionId = ":itemNoVersionId?",
      ) {
        return routes.unified.journals
          .section4(projectId, objectId, mainJournalId, titleId)
          .concat(`form/${itemNoVersionId || "new"}`);
      },
      section4View(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
        itemNoVersionId = ":itemNoVersionId",
      ) {
        return routes.unified.journals
          .section4(projectId, objectId, mainJournalId, titleId)
          .concat(`view/${itemNoVersionId}`);
      },
      section4Personnel(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
      ) {
        return routes.unified.journals
          .section4(projectId, objectId, mainJournalId, titleId)
          .concat("personnel/");
      },
      section5(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
      ) {
        return routes.unified.journals
          .root(projectId, objectId, mainJournalId)
          .concat(`${titleId}/section5/`);
      },
      section6(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
      ) {
        return routes.unified.journals
          .root(projectId, objectId, mainJournalId)
          .concat(`${titleId}/section6/`);
      },
      versions(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
      ) {
        return routes.unified.journals
          .root(projectId, objectId, mainJournalId)
          .concat(`versions/${titleId}/`);
      },
      journalVersionTitle(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
        mainJournalVersionId = ":mainJournalVersionId",
      ) {
        return routes.unified.journals
          .versions(projectId, objectId, mainJournalId, titleId)
          .concat(`${mainJournalVersionId}/titleJournal`);
      },
      journalVersionSection1(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
        mainJournalVersionId = ":mainJournalVersionId",
      ) {
        return routes.unified.journals
          .versions(projectId, objectId, mainJournalId, titleId)
          .concat(`${mainJournalVersionId}/section1/`);
      },
      journalVersionSection1Personnel(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
        mainJournalVersionId = ":mainJournalVersionId",
      ) {
        return routes.unified.journals
          .journalVersionSection1(
            projectId,
            objectId,
            mainJournalId,
            titleId,
            mainJournalVersionId,
          )
          .concat("personnel/");
      },
      journalVersionSection2(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
        mainJournalVersionId = ":mainJournalVersionId",
      ) {
        return routes.unified.journals
          .versions(projectId, objectId, mainJournalId, titleId)
          .concat(`${mainJournalVersionId}/section2/`);
      },
      journalVersionSection2Personnel(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
        mainJournalVersionId = ":mainJournalVersionId",
      ) {
        return routes.unified.journals
          .journalVersionSection2(
            projectId,
            objectId,
            mainJournalId,
            titleId,
            mainJournalVersionId,
          )
          .concat("personnel/");
      },
      journalVersionSection3(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
        mainJournalVersionId = ":mainJournalVersionId",
      ) {
        return routes.unified.journals
          .versions(projectId, objectId, mainJournalId, titleId)
          .concat(`${mainJournalVersionId}/section3/`);
      },
      journalVersionSection3Personnel(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
        mainJournalVersionId = ":mainJournalVersionId",
      ) {
        return routes.unified.journals
          .journalVersionSection3(
            projectId,
            objectId,
            mainJournalId,
            titleId,
            mainJournalVersionId,
          )
          .concat("personnel/");
      },
      journalVersionSection4(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
        mainJournalVersionId = ":mainJournalVersionId",
      ) {
        return routes.unified.journals
          .versions(projectId, objectId, mainJournalId, titleId)
          .concat(`${mainJournalVersionId}/section4/`);
      },
      journalVersionSection4Personnel(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
        mainJournalVersionId = ":mainJournalVersionId",
      ) {
        return routes.unified.journals
          .journalVersionSection4(
            projectId,
            objectId,
            mainJournalId,
            titleId,
            mainJournalVersionId,
          )
          .concat("personnel/");
      },
      journalVersionSection5(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
        mainJournalVersionId = ":mainJournalVersionId",
      ) {
        return routes.unified.journals
          .versions(projectId, objectId, mainJournalId, titleId)
          .concat(`${mainJournalVersionId}/section5/`);
      },
      journalVersionSection6(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
        mainJournalVersionId = ":mainJournalVersionId",
      ) {
        return routes.unified.journals
          .versions(projectId, objectId, mainJournalId, titleId)
          .concat(`${mainJournalVersionId}/section6/`);
      },
      titleVersionNew(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
      ) {
        return routes.unified.journals
          .title(projectId, objectId, mainJournalId, titleId)
          .concat(`/new`);
      },
      titleVersionDetail(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
        titleVersionId = ":titleVersionId",
      ) {
        return routes.unified.journals
          .title(projectId, objectId, mainJournalId, titleId)
          .concat(`/version/${titleVersionId}`);
      },
      journalVersions(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
      ) {
        return routes.unified.journals
          .root(projectId, objectId, mainJournalId)
          .concat(`version/${titleId}/`);
      },
      journalVersion(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
        versionId = ":versionId",
      ) {
        return routes.unified.journals
          .root(projectId, objectId, mainJournalId)
          .concat(`version/${titleId}/${versionId}/`);
      },
      titleVersions(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
      ) {
        return routes.unified.journals
          .title(projectId, objectId, mainJournalId, titleId)
          .concat(`/title-versions/`);
      },
      titleVersion(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
        titleVersionId = ":titleVersionId",
      ) {
        return routes.unified.journals
          .title(projectId, objectId, mainJournalId, titleId)
          .concat(`/title-versions/${titleVersionId}/`);
      },
      titleChangeRequests(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
      ) {
        return routes.unified.journals
          .title(projectId, objectId, mainJournalId, titleId)
          .concat(`/title-change-requests/`);
      },
      titleChangeRequestNew(
        projectId = ":projectId",
        objectId = ":objectId",
        mainJournalId = ":mainJournalId",
        titleId = ":titleId",
      ) {
        return routes.unified.journals
          .title(projectId, objectId, mainJournalId, titleId)
          .concat(`/title-change-requests/new/`);
      },
    },
    supervision: {
      root(projectId = ":projectId") {
        return routes.unified.project.root({ projectId }).concat("supervision/");
      },
      notifications(
        projectId = ":projectId",
        supervisionId = ":supervisionId",
      ) {
        return routes.unified.supervision.root(projectId).concat(`${supervisionId}/notifications/`);
      },
      adminSettings(
        projectId = ":projectId",
        supervisionId = ":supervisionId",
      ) {
        return routes.unified.supervision
          .root(projectId)
          .concat(`${supervisionId}/admin-settings/`);
      },
      integrationSettings(
        projectId = ":projectId",
        supervisionId = ":supervisionId",
      ) {
        return routes.unified.supervision
          .root(projectId)
          .concat(`${supervisionId}/integration-settings/`);
      },
    },
  },
};
